import { useEffect } from "react";
import { fetchFeedbackWidget } from "../api/client";
import { useAsyncData } from "./useAsyncData";

/** Where Marker.io's loader lives. The shim it fetches pulls in the widget itself. */
const SHIM_SRC = "https://edge.marker.io/latest/shim.js";

/** Marker.io reads its configuration off `window` before the shim runs — this is its contract. */
declare global {
  interface Window {
    markerConfig?: { project: string; source: string };
  }
}

/**
 * Loads the Marker.io bug-capture widget when the server says this deployment should have one.
 *
 * Which project (and whether there is one at all) is a *runtime* answer from
 * GET /api/feedback-widget, not a build-time `VITE_` variable: one built image serves every
 * environment here, so a baked-in id would put the widget in production too — which TomorrowToday
 * standards forbid. The server refuses to name a project when `ENV_TYPE` is PROD, so the widget's
 * absence there needs nothing from this code.
 *
 * Call it once, high in the tree. A failed fetch loads nothing, silently: the widget is for
 * reporting bugs in the app, and it must never become one an operator has to explain.
 */
export function useFeedbackWidget(): void {
  const { data } = useAsyncData(() => fetchFeedbackWidget(), []);
  const projectId = data?.project_id ?? "";

  useEffect(() => {
    if (!projectId || document.querySelector(`script[src="${SHIM_SRC}"]`)) {
      // Already loaded: StrictMode runs effects twice in development, and the shim is global —
      // a second copy would attach a second widget to the same page.
      return;
    }
    window.markerConfig = { project: projectId, source: "snippet" };
    const script = document.createElement("script");
    script.async = true;
    script.src = SHIM_SRC;
    document.head.appendChild(script);
    // Deliberately no cleanup: the widget owns DOM and listeners of its own that removing this
    // one tag would not undo, and the only unmount of this hook is the page going away.
  }, [projectId]);
}
